import useDevice from 'hooks/DesktopDevice.hook';
import { useSortedClaim } from 'hooks/useSortedClaim';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { Box, Stack, Typography } from 'styles';
import { theme } from 'styles/theme/ThemeStyle';
import { IPaymentClaim } from 'types/Payment';
import { useTranslation } from 'utils/translations';
import { formatPrice } from 'utils/utils';
import { PriceBox } from '../Payment.component';
import PaymentClaimsListItem from './PaymentClaimsListItem.component';

export const claimListRowSX = {
	justifyContent: 'space-between',
	alignItems: 'center',
	textAlign: 'center',
	gap: 2.5,

	[theme.breakpoints.up('lg')]: {
		gap: 0,
	},
};

export const boxListRowBoxSX = {
	minWidth: '100px',
	maxWidth: '100px',
};

const PaymentClaimsList = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { isDesktop } = useDevice();
	const { paymentDetails } = useSelector((store: RootState) => store.payment);

	const sortedList = useSortedClaim();

	const List = sortedList.map((paymentClaim: IPaymentClaim) => {
		return (
			<PaymentClaimsListItem
				paymentClaim={paymentClaim}
				key={paymentClaim.claimUUID}
			/>
		);
	});

	const progressIndicator = useSelector(
		(state: RootState) => state.progressIndicator,
	);

	return (
		<>
			{List.length > 0 ? (
				<Stack
					bgcolor={theme.palette.white.main}
					width={!isDesktop ? 'fit-content' : '100%'}
					px={2.5}
				>
					<Stack
						pt={2.5}
						pb={1.5}
						borderBottom={`1px solid ${theme.palette.grey5.main}`}
						justifyContent={'space-between'}
						gap={1.875}
					>
						{List}
					</Stack>
					<Stack flexDirection={'row'} sx={claimListRowSX} py={1.5}>
						<Box sx={boxListRowBoxSX} display={'flex'} justifyContent={'start'}>
							<Typography variant="value1">
								{tClientLabels('paymentTable.labels.total')}:
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}></Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.totalCharges)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.contractualWriteOff)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.memberResponsible)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.claimPayment)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.tax)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.retailNetOfTax)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.assessmentFee)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<PriceBox
								price={formatPrice(paymentDetails.total?.assessmentFeeTax)}
								isDesktop={isDesktop}
							/>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<PriceBox
								price={formatPrice(paymentDetails.total?.eyemedFee)}
								isDesktop={isDesktop}
							/>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.pswtAmount || 0)}
							</Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1">
								{formatPrice(paymentDetails.total?.netDue)}
							</Typography>
						</Box>
						<Box sx={{ minWidth: '140px', maxWidth: '140px' }}>
							<Typography variant="value1"></Typography>
						</Box>
						<Box sx={boxListRowBoxSX}>
							<Typography variant="value1"></Typography>
						</Box>
					</Stack>
				</Stack>
			) : (
				<Typography variant="bodyLink" sx={{ padding: 1.2 }}>
					{progressIndicator?.isLoading
						? ''
						: tClientLabels('payment.labels.noClaimFound')}
				</Typography>
			)}
		</>
	);
};

export default PaymentClaimsList;
